// src/components/VastuPanel.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { getDesignPlanFor } from '../utils/designStore';

/**
 * VastuPanel — a Vastu reading of the site and the traced plan.
 *
 * The design rule here is the same one UtilitiesPanel keeps, for a sharper
 * reason. Every line separates two things a reader will otherwise merge:
 *
 *   MEASURED   what VAUOM computed off the georeferenced boundary. The site
 *              polygon is stored in latitude and longitude, so the mandala is
 *              laid on TRUE north with nothing estimated — the one input a paper
 *              Vastu reading always has to guess at.
 *   THE TEXT   what the source book says, quoted, with its printed page.
 *   ASSUMED    a stand-in VAUOM supplied. Plan north is the important one: with
 *              no angle given, the top of the drawing is taken as north, and a
 *              wrong angle moves every room by up to a whole quarter.
 *
 * And it never grades what the text does not grade. Most of the mandala names a
 * position and attaches no judgement to it; those read as REPORTED, with the
 * reason. Filling that gap from general Vastu knowledge would be easier here
 * than anywhere else in the app, because the reader arrives expecting a verdict
 * — which is exactly why the panel refuses to.
 *
 * Vastu is a traditional doctrine, not a building regulation. Nothing in this
 * panel is a compliance result and none of it touches the DCR verdicts.
 */

const T = {
  surface: 'var(--surface-2,#1a2234)',
  surfaceHi: 'var(--surface-3,#212a3e)',
  border: 'var(--border,#3a4456)',
  text: 'var(--text,#e6ebf5)',
  textDim: 'var(--text-dim,#9aa7c0)',
  accent: 'var(--accent,#5b9dff)',
  ok: 'var(--ok,#3ecf8e)',
  warn: 'var(--warn,#f0b429)',
  err: 'var(--err,#f0616d)',
  ai: 'var(--ans-cite,#7a44b8)',
};

const TIER_BADGE = {
  measured: { label: 'MEASURED', color: T.ok, title: 'Computed from this project’s own geometry' },
  assumed:  { label: 'ASSUMED',  color: T.warn, title: 'A VAUOM default standing in until a real value is given' },
  missing:  { label: 'NOT YET',  color: T.textDim, title: 'Something is needed before this can be read' },
};

const GRADE_COLOUR = {
  aligned: T.ok, square: T.ok, clear: T.ok,
  slight: T.ok, oblong: T.textDim,
  marked: T.warn, elongated: T.warn,
  diagonal: T.err, obstructed: T.warn,
};

// The eight quarters as they sit on screen: north at the top, matching the
// compass in the 3D view rather than the row order the grid is computed in.
const GRID_ORDER = [
  ['NW', 'N', 'NE'],
  ['W', 'C', 'E'],
  ['SW', 'S', 'SE'],
];

function Badge({ tier, report }) {
  if (report) {
    return (
      <span style={{ ...S.badge, color: T.ai, borderColor: T.ai }}
            title="The text names this position but attaches no judgement to it, so none is shown">
        REPORTED
      </span>
    );
  }
  const b = TIER_BADGE[tier] || TIER_BADGE.missing;
  return <span style={{ ...S.badge, color: b.color, borderColor: b.color }} title={b.title}>{b.label}</span>;
}

function Finding({ f }) {
  return (
    <div style={S.card}>
      <div style={S.cardHead}>
        <span style={S.cardTitle}>{f.title}</span>
        <Badge tier={f.tier} report={f.report} />
        {f.grade && (
          <span style={{ ...S.grade, color: GRADE_COLOUR[f.grade] || T.textDim }}>{f.grade}</span>
        )}
      </div>

      {f.measured && <div style={S.measured}>{f.measured}</div>}
      {f.detail && <div style={S.detail}>{f.detail}</div>}

      {f.says && <div style={S.says}>{f.says}</div>}
      {f.quote && <div style={S.quote}>&ldquo;{f.quote}&rdquo;</div>}
      {f.rationale && <div style={S.detail}>{f.rationale}</div>}
      {f.notChecked && <div style={S.notChecked}>{f.notChecked}</div>}

      {f.action && <div style={S.action}>To read it: {f.action}</div>}
      {f.cite && (
        <div style={S.cite}>
          Vastu-Sastra Vol. I, p.{f.cite.page}
          {f.cite.also ? ` and p.${f.cite.also}` : ''}
          {f.cite.text ? ` (${f.cite.text})` : ''}
        </div>
      )}
    </div>
  );
}

export default function VastuPanel({ project, apiBase, inputs }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  // Plan north is the one input the drawing cannot supply: a tracing has no
  // compass in it. Null means "not stated", which the backend reports as an
  // assumption rather than silently treating as zero.
  const [planNorth, setPlanNorth] = useState('');

  const read = useCallback(async () => {
    if (!project?.polygonLatLng?.length) { setData(null); return; }
    setBusy(true); setErr(null);
    try {
      const res = await fetch(`${apiBase}/api/vastu/read`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          polygonLatLng: project.polygonLatLng,
          buildingHeightM:
            Number(inputs?.achieved?.exterior?.height?.value) ||
            Number(project?.siteData?.heightLimit) || null,
          rooms: tracedRooms(project?.id),
          planNorthDeg: planNorth === '' ? null : Number(planNorth),
        }),
      });
      const d = await res.json();
      if (!d.ok && d.error) setErr(d.error);
      setData(d);
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  }, [apiBase, project, inputs, planNorth]);

  useEffect(() => { read(); }, [read]);

  if (!project?.polygonLatLng?.length) {
    return <div style={S.empty}>Trace or sketch the site boundary first — the reading is laid out on the plot.</div>;
  }

  const site = data?.site;
  const plan = data?.plan;

  return (
    <div style={S.wrap}>
      <div style={S.intro}>
        A reading from <b>{data?.source?.title || 'the source text'}</b>
        {data?.source?.author ? ` (${data.source.author})` : ''}. It states what the book says, quoted and
        cited to its printed page, beside what VAUOM measured. <b>It is not a building regulation</b> and
        nothing here affects the compliance check.
        {busy && <span style={S.busy}> reading…</span>}
      </div>

      {err && <div style={S.err}>{err}</div>}

      {site?.site && (
        <div style={S.statRow}>
          <Stat label="Plot area" value={`${site.site.areaM2} m²`} />
          <Stat label="Off cardinal" value={`${site.site.offCardinalDeg}°`} />
          <Stat label="Cardinal box" value={`${site.site.box.widthM} × ${site.site.box.depthM} m`} />
        </div>
      )}

      <h4 style={S.h}>The site</h4>
      {(site?.findings || []).map((f) => <Finding key={f.id} f={f} />)}

      <h4 style={S.h}>The plan</h4>
      <div style={S.northRow}>
        <label style={S.northLabel} htmlFor="vastu-plan-north">Plan north</label>
        <input
          id="vastu-plan-north" type="number" style={S.northInput}
          value={planNorth} placeholder="not stated"
          onChange={(e) => setPlanNorth(e.target.value)}
        />
        <span style={S.northHint}>
          degrees the drawing&rsquo;s &ldquo;up&rdquo; is turned from true north.
          {plan?.planNorth?.tier === 'assumed' && ' Until it is set, the top of the sheet is taken as north.'}
        </span>
      </div>

      {plan?.ok ? (
        <>
          {plan.planNorth?.tier === 'assumed' && (
            <div style={S.assumeNote}>{plan.planNorth.basis}</div>
          )}
          <div style={S.roomTable}>
            <div style={{ ...S.rRow, ...S.rHead }}>
              <span>Room</span><span>Quarter</span><span>Sanskrit</span><span>Pada deity</span>
            </div>
            {plan.rooms.map((r) => (
              <div key={r.n} style={S.rRow}>
                <span style={S.rName}>{r.name}</span>
                <span>{r.quarterLabel}</span>
                <span style={S.rSans}>{r.sanskrit || '—'}</span>
                <span style={S.rDeity}>{r.deity || '—'}</span>
              </div>
            ))}
          </div>
          <div style={S.says}>{plan.note}</div>
          {plan.cite && <div style={S.cite}>Vastu-Sastra Vol. I, p.{plan.cite.page}</div>}
          {(plan.findings || []).map((f) => <Finding key={f.id} f={f} />)}
        </>
      ) : (
        <div style={S.empty}>{plan?.error || 'No traced plan yet.'}</div>
      )}

      {data?.mandala && (
        <>
          <h4 style={S.h}>{data.mandala.name}</h4>
          <div style={S.says}>{data.mandala.purpose}</div>
          <div style={S.mandala}>
            {GRID_ORDER.map((row, i) => (
              <div key={i} style={S.mRow}>
                {row.map((k) => (
                  <div key={k} style={{ ...S.mCell, ...(k === 'C' ? S.mCentre : null) }}>
                    <div style={S.mKey}>{k === 'C' ? 'Brahma' : k}</div>
                    <div style={S.mDeity}>
                      {k === 'C'
                        ? `${data.mandala.brahmasthana.padas} padas`
                        : (data.mandala.cardinalLords.find((l) => l.direction === k) || {}).deity || ''}
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
          <div style={S.quote}>&ldquo;{data.mandala.brahmasthana.quote}&rdquo;</div>
          <div style={S.says}>{data.mandala.brahmasthana.readingNote}</div>
        </>
      )}

      {data?.notEncoded && (
        <>
          <h4 style={S.h}>What this reading deliberately leaves out</h4>
          {Object.entries(data.notEncoded).map(([k, v]) => (
            <div key={k} style={S.omit}>
              <div style={S.omitWhat}>{v.what}</div>
              <div style={S.omitWhy}>{v.why}</div>
            </div>
          ))}
        </>
      )}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div style={S.stat}>
      <div style={S.statVal}>{value}</div>
      <div style={S.statLab}>{label}</div>
    </div>
  );
}

/**
 * The traced rooms for THIS project, read at the moment of asking.
 *
 * Goes through the project-stamped accessor, so a plan belonging to another
 * project cannot be read here — the reading would be confidently about the
 * wrong building.
 *
 * The border outline is dropped: the parser emits it as a room, and it spans the
 * whole sheet, so its centroid always lands on the Brahmasthana and would report
 * a room that is not one.
 */
function tracedRooms(projectId) {
  const plan = getDesignPlanFor(projectId);
  return (plan?.rooms || []).filter((r) => r && r.kind !== 'border');
}

const S = {
  wrap: { display: 'flex', flexDirection: 'column', gap: 10, padding: '4px 2px 16px' },
  intro: { fontSize: 11.5, lineHeight: 1.55, color: T.textDim },
  busy: { color: T.accent },
  err: { fontSize: 11.5, color: T.err, border: `1px solid ${T.err}`, borderRadius: 8, padding: '6px 9px' },
  empty: { fontSize: 12, color: T.textDim, padding: '10px 2px' },
  h: { margin: '10px 0 2px', fontSize: 12, fontWeight: 700, color: T.text, letterSpacing: 0.3 },

  statRow: { display: 'flex', gap: 8 },
  stat: { flex: 1, background: T.surfaceHi, border: `1px solid ${T.border}`, borderRadius: 9, padding: '7px 9px' },
  statVal: { fontSize: 14, fontWeight: 700, color: T.text },
  statLab: { fontSize: 9.5, color: T.textDim, letterSpacing: 0.3, textTransform: 'uppercase' },

  card: { background: T.surface, border: `1px solid ${T.border}`, borderRadius: 10, padding: '8px 10px' },
  cardHead: { display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap' },
  cardTitle: { fontSize: 12, fontWeight: 700, color: T.text, flex: 1, minWidth: 0 },
  grade: { fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4 },
  badge: { fontSize: 8.5, fontWeight: 700, letterSpacing: 0.5, padding: '1px 5px',
           borderRadius: 5, border: '1px solid', whiteSpace: 'nowrap' },
  measured: { fontSize: 12.5, color: T.text, marginTop: 4, fontWeight: 600 },
  detail: { fontSize: 11, color: T.textDim, marginTop: 3, lineHeight: 1.5 },
  says: { fontSize: 11, color: T.textDim, marginTop: 4, lineHeight: 1.55 },
  quote: { fontSize: 11.5, color: T.text, marginTop: 5, paddingLeft: 8,
           borderLeft: `2px solid ${T.ai}`, fontStyle: 'italic', lineHeight: 1.5 },
  notChecked: { fontSize: 10.5, color: T.warn, marginTop: 4 },
  action: { fontSize: 11, color: T.accent, marginTop: 4 },
  cite: { fontSize: 10, color: T.textDim, marginTop: 5, opacity: 0.85 },

  northRow: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' },
  northLabel: { fontSize: 11.5, color: T.text, fontWeight: 600 },
  northInput: { width: 78, boxSizing: 'border-box', fontSize: 12, padding: '4px 7px',
                borderRadius: 7, border: `1px solid ${T.border}`,
                background: 'var(--field,#20293c)', color: T.text },
  northHint: { fontSize: 10.5, color: T.textDim, flex: 1, minWidth: 160 },
  assumeNote: { fontSize: 11, color: T.warn, lineHeight: 1.5 },

  roomTable: { border: `1px solid ${T.border}`, borderRadius: 9, overflow: 'hidden' },
  rRow: { display: 'grid', gridTemplateColumns: 'minmax(0,1.2fr) minmax(0,1.3fr) minmax(0,1fr) minmax(0,1.2fr)',
          gap: 6, padding: '5px 9px', fontSize: 11.5, color: T.text,
          borderTop: `1px solid ${T.border}` },
  rHead: { borderTop: 'none', background: T.surfaceHi, fontSize: 10, letterSpacing: 0.3,
           textTransform: 'uppercase', color: T.textDim, fontWeight: 700 },
  rName: { fontWeight: 600 },
  rSans: { fontStyle: 'italic', color: T.textDim },
  rDeity: { color: T.ai },

  mandala: { display: 'flex', flexDirection: 'column', gap: 3 },
  mRow: { display: 'flex', gap: 3 },
  mCell: { flex: 1, background: T.surfaceHi, border: `1px solid ${T.border}`, borderRadius: 7,
           padding: '7px 5px', textAlign: 'center', minWidth: 0 },
  mCentre: { borderColor: T.ai },
  mKey: { fontSize: 11, fontWeight: 700, color: T.text },
  mDeity: { fontSize: 9.5, color: T.textDim, marginTop: 1 },

  omit: { background: T.surface, border: `1px dashed ${T.border}`, borderRadius: 9, padding: '7px 10px' },
  omitWhat: { fontSize: 11.5, color: T.text, fontWeight: 600, lineHeight: 1.5 },
  omitWhy: { fontSize: 11, color: T.textDim, marginTop: 3, lineHeight: 1.55 },
};
